// ----------------------------------------------------------------
// <copyright company="Microsoft Corporation">
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

export interface FilterOptions {
    searchText: string;
    filters?: {
        [key: string]: string[];
    };
}

function search(val: any, searchText: string) {
    return val !== null &&
        ['undefined'].indexOf(typeof val) === -1 &&
        JSON.stringify(val).toUpperCase().indexOf(searchText.toUpperCase()) !== -1;
}

export function applyColumnFilters<T>(items: T[], filters: { [key: string]: string[] }): T[] {
    let newValues: T[] = [...items];
    Object.keys(filters).forEach((key) => {
        newValues = filterByKeyByString(newValues, key, filters[key]);
    });
    return newValues;
}

/**
 * Search an array of objects for keyword matches.
 * @param items Array of objects to search. Can have nested objects to search.
 * @param keywords Keywords search, space delimited.
 */
export function filterByKeywords(items: any[], keywords: string): any[] {
    if (!keywords) {
        return items;
    }

    // Whenever we encounter a space in our search,
    // we will filter for both the first and second word (or third word)
    const needleAry = keywords.split(' ').filter(x => x);

    // For each needle in our array, cycle through the data (row[key]) in the current row looking for a match.
    return items.filter(row =>
        needleAry.every(needle =>
            Object.keys(row).some(key =>
                search(row[key], needle))));
}

/**
 * Search an array of objects for full text matches
 * @param items An array of objects that have strings in them to be searched for the searchText
 * @param searchText Text to search for. Matches will be a full match, not a keyword match.
 */
export function filterByString(items: any[], searchText: string): any[] {
    if (!searchText) {
        return items;
    }

    return items.filter(row =>
        Object.keys(row).some(key =>
            search(row[key], searchText)));
}

/**
 * Filter by a specific key in each item in the array by a specific string search
 * This will perform a case-insensitive search any of the full strings specified
 */
export function filterByKeyByString(items: any[], key: string, searchOptions: string[]): any[] {
    if (!searchOptions || !searchOptions.length || !searchOptions[0]) {
        return items;
    }

    return items.filter((row: string[]) =>
        searchOptions.some((searchText) =>
            (row[key] && row[key].toString().toUpperCase()) === (searchText && searchText.toString().toUpperCase())));
}

export function applySearchToList(searchText: string, listOfItems: string[]) {
    return listOfItems.filter(item => item && search(item, searchText));
}

export function filterData(dataTableItems: any[], filterOptions: FilterOptions) {
    const filtered = applyColumnFilters(dataTableItems, filterOptions.filters || {});
    return filterByKeywords(filtered, filterOptions.searchText);
}